import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../../assets/assetKeys';
import { VISUAL_THEME } from '../../assets/visualTheme';
import { GAME_HEIGHT, GAME_WIDTH } from '../../data/gameConstants';
import { ObstacleRect } from '../../systems/ZoneCoreSystems';
import { UIStyle } from '../../ui/UIStyle';

export const ZONE3_POSITIONS = {
  SPAWN: { x: 180, y: 580 },
  HOUSE: { x: 480, y: 420 },
  HOUSE_APPROACH: { x: 480, y: 500 },
  HOUSE_PLATFORM: { x: 480, y: 360 },
  SIGN: { x: 760, y: 480 },
  KERIS: { x: 340, y: 535 },
  BAG_STATION: { x: 940, y: 520 },
  EXIT: { x: 1180, y: 530 },
  EXIT_BLOCK: { x: 1080, y: 530, width: 40, height: 200 },
} as const;

export class Zone3Map {
  private scene: Phaser.Scene;
  private exitBlocker?: Phaser.GameObjects.Rectangle;
  private exitLabel?: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  build(): ObstacleRect[] {
    this.drawBackground();
    this.drawYard();
    this.drawHouse();
    this.drawDecorations();
    return this.buildObstacles();
  }

  openExitPath(): void {
    this.exitBlocker?.destroy();
    this.exitBlocker = undefined;
    this.exitLabel?.setColor('#52b788');
    this.exitLabel?.setText('→ Persimpangan Jalur (Terbuka)');
  }

  private drawBackground(): void {
    this.scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, TEXTURE_KEYS.BG_ZONE3)
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
      .setDepth(0);

    this.scene.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT,
      VISUAL_THEME.zoneWarmth.zone3, 0.08,
    ).setDepth(0.5);

    const sky = this.scene.add.graphics().setDepth(1);
    sky.fillStyle(0x9ee5ff, 0.25);
    sky.fillRect(0, 0, GAME_WIDTH, 280);
    sky.fillStyle(0xf2c14e, 0.08);
    sky.fillCircle(1020, 96, 42);
  }

  private drawYard(): void {
    const yard = this.scene.add.graphics().setDepth(2);
    yard.fillStyle(0xa1887f, VISUAL_THEME.pathAlpha);
    yard.fillEllipse(GAME_WIDTH / 2, 560, 1100, 180);
    yard.fillStyle(0xc4a882, 0.35);
    yard.fillEllipse(500, 540, 400, 120);
    yard.fillStyle(0xe7c487, 0.18);
    yard.fillEllipse(950, 545, 260, 80);

    const path = this.scene.add.graphics().setDepth(2);
    path.fillStyle(0x8d6e63, 0.62);
    const pts = [
      { x: 120, y: 590 },
      { x: 350, y: 560 },
      { x: 600, y: 530 },
      { x: 850, y: 520 },
      { x: 1100, y: 540 },
    ];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      path.fillEllipse((a.x + b.x) / 2, (a.y + b.y) / 2, 100, 36);
    }
  }

  private drawHouse(): void {
    const house = ZONE3_POSITIONS.HOUSE;
    this.scene.add.image(house.x, house.y, TEXTURE_KEYS.HOUSE_BUBUNGAN)
      .setScale(1.35)
      .setDepth(4);

    const g = this.scene.add.graphics().setDepth(3);
    g.fillStyle(0x5d4037, 0.6);
    g.fillRect(house.x - 90, house.y + 50, 180, 12);

    for (let i = 0; i < 5; i++) {
      const px = house.x - 70 + i * 35;
      g.fillStyle(0x3e2723, 0.8);
      g.fillRect(px, house.y + 20, 10, 42);
    }

    g.fillStyle(0x6d4c41, 0.7);
    g.fillTriangle(house.x - 30, house.y + 62, house.x, house.y + 30, house.x + 30, house.y + 62);
    g.fillStyle(0xe8d8b5, 0.7);
    g.fillRect(house.x - 80, house.y + 56, 160, 6);
    g.fillStyle(0x3e2723, 0.6);
    g.fillRect(house.x - 44, house.y + 10, 88, 8);

    this.scene.add.text(house.x, house.y - 100, 'Rumah Bubungan Lima', {
      ...UIStyle.mapLabel(16, '#ffd966'),
    }).setOrigin(0.5).setDepth(5);
  }

  private drawDecorations(): void {
    const sign = ZONE3_POSITIONS.SIGN;
    this.scene.add.image(sign.x, sign.y - 10, TEXTURE_KEYS.SIGN_BOARD).setScale(1.1).setDepth(4);
    this.scene.add.text(sign.x, sign.y - 55, 'Papan Informasi', {
      ...UIStyle.mapLabel(14, '#ffd966'),
    }).setOrigin(0.5).setDepth(5);
    this.scene.add.image(ZONE3_POSITIONS.KERIS.x, ZONE3_POSITIONS.KERIS.y, TEXTURE_KEYS.KERIS_2)
      .setScale(0.86).setDepth(4).setAngle(-12);

    const bag = ZONE3_POSITIONS.BAG_STATION;
    this.scene.add.rectangle(bag.x, bag.y, 90, 50, 0x8d6e63, 0.5).setDepth(3);
    this.scene.add.rectangle(bag.x - 18, bag.y + 2, 18, 22, 0xc96f3b, 0.95).setDepth(4);
    this.scene.add.rectangle(bag.x + 2, bag.y + 2, 18, 22, 0x3b82f6, 0.95).setDepth(4);
    this.scene.add.rectangle(bag.x + 22, bag.y + 2, 18, 22, 0xef4444, 0.95).setDepth(4);
    this.scene.add.text(bag.x, bag.y - 35, 'Area Tas Siaga', {
      ...UIStyle.mapLabel(14, '#ffffff'),
    }).setOrigin(0.5).setDepth(5);

    this.scene.add.ellipse(300, 500, 60, 35, 0x40916c, 0.65).setDepth(3);
    this.scene.add.ellipse(650, 490, 50, 30, 0x52b788, 0.55).setDepth(3);
    this.scene.add.image(220, 585, TEXTURE_KEYS.GROUND_CRACK).setScale(0.55).setDepth(2.5).setAlpha(0.65);
    this.scene.add.text(bag.x, bag.y + 42, 'Bekal siaga keluarga', {
      ...UIStyle.mapLabel(14, '#f5f5f5'),
    }).setOrigin(0.5).setDepth(5);
    this.exitLabel = UIStyle.addMapLabel(
      this.scene,
      ZONE3_POSITIONS.EXIT.x,
      ZONE3_POSITIONS.EXIT.y - 50,
      '→ Persimpangan (Terkunci)',
      15,
      '#ff6b6b',
    ).setDepth(5);
  }

  private buildObstacles(): ObstacleRect[] {
    const house = ZONE3_POSITIONS.HOUSE;
    const obs: ObstacleRect[] = [
      // Langit + bukit — blok seluruh area atas (sama seperti Zona 1 & 2)
      { x: GAME_WIDTH / 2, y: 165, width: GAME_WIDTH, height: 330 },
      // Lantai koridor — cegah turun ke bawah peta
      { x: GAME_WIDTH / 2, y: GAME_HEIGHT - 20, width: GAME_WIDTH, height: 40 },
      // Dinding kiri/kanan
      { x: 30, y: GAME_HEIGHT / 2, width: 60, height: GAME_HEIGHT },
      { x: GAME_WIDTH - 20, y: GAME_HEIGHT / 2, width: 40, height: GAME_HEIGHT },
      // Semak/bukit latar — blok naik ke lereng
      { x: 170, y: 405, width: 320, height: 110 },
      { x: 680, y: 398, width: 460, height: 110 },
      { x: 1085, y: 405, width: 290, height: 110 },
      // Pohon/dekor di tepi jalur
      { x: 160, y: 430, width: 60, height: 80 },
      { x: 720, y: 400, width: 50, height: 70 },
      { x: 1040, y: 405, width: 55, height: 75 },
      { x: 1160, y: 425, width: 50, height: 70 },
      // Penghalang lereng — tutup celah naik ke bukit
      { x: 820, y: 430, width: 280, height: 90 },
      { x: 560, y: 430, width: 120, height: 70 },
      { x: 395, y: 395, width: 150, height: 100 },
      // Rumah bubungan — cegah naik ke atap/platform
      { x: house.x, y: house.y - 30, width: 140, height: 50 },
      { x: house.x, y: house.y + 10, width: 120, height: 30 },
      { x: house.x - 55, y: house.y + 35, width: 25, height: 50 },
      { x: house.x + 55, y: house.y + 35, width: 25, height: 50 },
      { x: ZONE3_POSITIONS.SIGN.x, y: ZONE3_POSITIONS.SIGN.y + 10, width: 35, height: 40 },
    ];

    const fb = ZONE3_POSITIONS.EXIT_BLOCK;
    this.exitBlocker = this.scene.add.rectangle(fb.x, fb.y, fb.width, fb.height, 0xc1121f, 0.35);
    this.exitBlocker.setDepth(6);
    obs.push({ x: fb.x, y: fb.y, width: fb.width, height: fb.height });

    return obs;
  }
}
